/**
 * Registro de widgets: nombre -> componente.
 *
 * Es el punto de extension del renderer. Un modulo que necesite un widget nuevo
 * (un selector de plan de cuentas, un mapa, una firma) lo registra con
 * {@link registerFieldWidget} y lo declara en su `FieldSpec(widget="...")`. El
 * renderer no cambia ni una linea.
 *
 * La resolucion tiene tres pasos, en este orden:
 *
 * 1. `meta.widget`, si esta registrado. Es lo que el modulo pidio explicitamente.
 * 2. El tipo del campo (`many2one`, `date`, `boolean`...).
 * 3. `char`, que muestra el valor sin romper la pantalla.
 *
 * Que un widget desconocido degrade a texto en vez de tirar la pantalla abajo es
 * deliberado: un `FieldSpec` con un typo tiene que ser un campo feo, no un ERP
 * en blanco.
 */

import { AreaField } from '@/components/fields/AreaField';
import { BooleanField } from '@/components/fields/BooleanField';
import { CharField } from '@/components/fields/CharField';
import { ColorField } from '@/components/fields/ColorField';
import { DateField } from '@/components/fields/DateField';
import { DateTimeField } from '@/components/fields/DateTimeField';
import { FloatField } from '@/components/fields/FloatField';
import { HoursField } from '@/components/fields/HoursField';
import { IntegerField } from '@/components/fields/IntegerField';
import { JsonField } from '@/components/fields/JsonField';
import { Many2ManyField } from '@/components/fields/Many2ManyField';
import { Many2OneField } from '@/components/fields/Many2OneField';
import { MonetaryField } from '@/components/fields/MonetaryField';
import { One2ManyField } from '@/components/fields/One2ManyField';
import { PasswordField } from '@/components/fields/PasswordField';
import { ConsorcioDeLaVentaField } from '@/components/fields/ConsorcioDeLaVentaField';
import { ProductoVendidoField } from '@/components/fields/ProductoVendidoField';
import { SelectionField } from '@/components/fields/SelectionField';
import { StatusBarField } from '@/components/fields/StatusBarField';
import { StatusField } from '@/components/fields/StatusField';
import { TextField } from '@/components/fields/TextField';
import { TimeField } from '@/components/fields/TimeField';
import { TipoDeContactoField } from '@/components/fields/TipoDeContactoField';
import type { FieldInputComponent } from '@/components/fields/types';
import type { FieldMeta } from '@/lib/types';

const REGISTRO = new Map<string, FieldInputComponent>([
  ['char', CharField],
  ['email', CharField],
  ['url', CharField],
  ['text', TextField],
  ['textarea', TextField],
  ['html', TextField],
  // Clave de una plataforma externa anotada en una ficha (MIBA, ARCA). Sin esta
  // linea, `widget="password"` degradaria a `char` —o sea a un input de texto
  // comun— avisando solo por consola y solo en DEV: la clave se dibujaria a la
  // vista y nadie se enteraria. Su mitad de solo lectura esta en `FieldDisplay`.
  ['password', PasswordField],
  ['integer', IntegerField],
  ['float', FloatField],
  ['decimal', FloatField],
  ['monetary', MonetaryField],
  ['money', MonetaryField],
  ['percent', FloatField],
  // Solo lectura en todos los modelos que lo declaran (`crm.lead.score`): la
  // barra la dibuja `FieldDisplay`; el control editable es el entero de abajo.
  ['progress', IntegerField],
  // Magnitud con unidad: `84,50 m²`. La unidad la declara el modulo.
  ['area', AreaField],
  // Duracion decimal con su lectura en `h`/`min` debajo.
  ['hours', HoursField],
  ['boolean', BooleanField],
  ['switch', BooleanField],
  ['checkbox', BooleanField],
  ['date', DateField],
  ['datetime', DateTimeField],
  ['time', TimeField],
  ['selection', SelectionField],
  // Escala corta declarada con `choices`: se edita como una lista cerrada y se
  // muestra como pastilla (ver `FieldDisplay`).
  ['priority', SelectionField],
  // Indice de paleta (`integer`) o `#RRGGBB` (`char`): el mismo widget resuelve
  // las dos formas mirando el tipo del campo.
  ['color', ColorField],
  // `badge` es la pastilla del estado actual; `status` es el circuito completo
  // con la etapa activa marcada. Los dos son semaforos, con distinto alcance.
  ['badge', StatusField],
  ['status', StatusBarField],
  ['many2one', Many2OneField],
  ['one2many', One2ManyField],
  ['many2many', Many2ManyField],
  ['json', JsonField],
  // El catalogo de venta de un cliente, con el precio que le corresponde a EL y
  // cada cuanto vence cada cosa. Es un many2one a `products.product` que el
  // selector generico no puede dibujar: `RelationPicker` no acepta dominio (asi
  // que ofreceria archivados, no vendibles y de otras empresas) y sobre todo
  // mostraria solo el nombre, cuando lo que se pidio fue el catalogo CON LOS
  // PRECIOS.
  ['catalogo', ProductoVendidoField],
  // «¿Que es este contacto?»: el mismo booleano `is_company`, preguntado con
  // palabras y con las opciones que le agrega el modulo del oficio. No es un
  // `selection` generico porque ademas de escribir el campo tiene que crear la
  // fila que hace verdadera la opcion elegida —la ficha de la administracion, el
  // vinculo con el edificio— y mostrar lo que el sistema DEDUJO del contacto.
  // «¿Para qué consorcio?» del formulario de la venta. Es una columna ENTERA y
  // opaca de `sales` —no una FK— asi que el `many2one` generico no la puede
  // dibujar: se guia por `meta.relation`, que acá no existe ni puede existir.
  // Ver el docstring del componente.
  ['consorcio_de_la_venta', ConsorcioDeLaVentaField],
  ['tipo_de_contacto', TipoDeContactoField],
]);

/**
 * Widgets que reciben los campos HERMANOS del formulario (`FieldInputProps.siblings`).
 *
 * Es una lista blanca corta y explicita, no una capacidad general: escribir
 * campos que no son el propio es lo que permite que elegir un producto complete
 * el importe y el vencimiento delante del usuario, y tambien lo que podria
 * romper cualquier otra pantalla del sistema si se repartiera a todos. Un widget
 * que no esta aca recibe `undefined` y no cambia de conducta.
 */
const CON_HERMANOS = new Set<string>([
  'catalogo',
  // Lee `client_kind` —lo que el sistema DEDUJO del contacto, que es lo unico
  // que no puede mentir— y `cuit`, para avisar de la unica pared del alta antes
  // de que la persona apriete «Crear». Escribe `is_customer`, porque el motivo
  // entero de que la ficha de una administracion o de un consorcio exista es
  // poder venderle.
  'tipo_de_contacto',
]);

/** `true` si el widget declaro que necesita leer y escribir campos hermanos. */
export function widgetUsaHermanos(nombre: string): boolean {
  return CON_HERMANOS.has(nombre);
}

/** Agrega o reemplaza un widget. Llamalo en el arranque del modulo que lo aporta. */
export function registerFieldWidget(nombre: string, componente: FieldInputComponent): void {
  REGISTRO.set(nombre, componente);
}

/** `true` si el nombre corresponde a un widget conocido. */
export function hasFieldWidget(nombre: string): boolean {
  return REGISTRO.has(nombre);
}

/**
 * Widgets pedidos por la metadata que nadie registro. Se avisa UNA vez por
 * nombre y solo en desarrollo.
 *
 * La degradacion a texto sigue siendo deliberada —un typo tiene que ser un campo
 * feo, no un ERP en blanco— pero degradar **en silencio** hizo que trece campos
 * de `services`, `products` y `crm` se dibujaran mal durante toda una ola de
 * modulos sin que nadie se enterara. Que sea visible en la consola es la
 * diferencia entre una decision y un descuido.
 */
const YA_AVISADOS = new Set<string>();

function avisarWidgetDesconocido(nombre: string): void {
  if (!import.meta.env.DEV || YA_AVISADOS.has(nombre)) return;
  YA_AVISADOS.add(nombre);
  console.warn(
    `[nucleo] La metadata pide el widget «${nombre}», que no esta en el registro. ` +
      'Se dibuja con el widget del tipo del campo. Registralo en components/fields/registry.ts.',
  );
}

/** Nombre del widget que le toca a un campo, resuelto con las tres reglas de arriba. */
export function resolveWidgetName(meta: FieldMeta): string {
  if (meta.widget !== undefined && meta.widget !== '') {
    if (REGISTRO.has(meta.widget)) return meta.widget;
    avisarWidgetDesconocido(meta.widget);
  }

  // Un campo con `choices` es una lista cerrada aunque su tipo sea `char`.
  if (meta.choices !== undefined && meta.choices.length > 0 && meta.type !== 'many2one') {
    return 'selection';
  }

  if (REGISTRO.has(meta.type)) return meta.type;
  return 'char';
}

export function getFieldInput(meta: FieldMeta): FieldInputComponent {
  return REGISTRO.get(resolveWidgetName(meta)) ?? CharField;
}
